// ui/rm-upgrade-card.js — the upsell modal (Brief 005a).
//
// Shown by `<rm-player>` in place of the transport when a user without
// entitlement opens a locked audio item. Mirrors the SpeechGenie upsell:
// headline, "what you unlock" copy, a single CTA, and a dismiss.
//
// Zero deps (CLAUDE.md Rule #5): inline SVG from `src/ui/icons.js`, no icon
// font, no modal library. Accessibility: `role="dialog"`/`aria-modal`, focus
// moves into the card on mount, Tab is trapped inside it, and Esc (or the
// browser Back button via the host screen) dismisses.
//
// The CTA opens `STRIPE_CHECKOUT_URL` — injected by esbuild `--define`
// (`__STRIPE_CHECKOUT_URL__`) in production; a `#` placeholder in dev until the
// real Stripe checkout exists (Brief 005b / 013).

import { RMComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { LOCK, CLOSE } from "./icons.js";

/** Stripe checkout URL — build-time define; `#` placeholder in dev/unbundled. */
const STRIPE_CHECKOUT_URL =
  typeof __STRIPE_CHECKOUT_URL__ !== "undefined" ? __STRIPE_CHECKOUT_URL__ : "#";

/** "Already purchased? Sign in" target — the WordPress login page that emails a
 *  magic link so a buyer can restore access on a new device / incognito. Shown
 *  on the card because that's the "no access" moment. Build-time define. */
const SIGN_IN_URL =
  typeof __SIGN_IN_URL__ !== "undefined"
    ? __SIGN_IN_URL__
    : "https://speechgenie.co/my-speech-genie-lessons-login/";

/**
 * @typedef {"locked"|"expired"} UpgradeReason
 */

/** @type {Record<UpgradeReason, { headline: string, body: string, cta: string }>} */
const COPY = {
  locked: {
    headline: "Unlock every lesson",
    body: "You're listening to the free set. Unlock the full Rhythmic Mandarin course — every lesson, in both Pinyin and simplified-Chinese tracks.",
    cta: "Unlock full access — just $47",
  },
  expired: {
    headline: "Your access has expired",
    body: "Renew to keep playing every lesson — your progress is saved and waiting for you.",
    cta: "Renew access",
  },
};

export class RMUpgradeCard extends RMComponent {
  static props = ["itemId", "reason", "embedded"];
  /** @type {string | null} */ itemId = null;
  /** @type {UpgradeReason} */ reason = "locked";
  /**
   * When false (default) the card is route content (the player gate): dismiss
   * navigates back. When true the card is mounted as an in-place overlay (the
   * `<rm-list>` locked-row upsell), so dismiss only emits `dismiss` and the
   * host removes the overlay — no navigation. Brief 018.
   * @type {boolean}
   */
  embedded = false;

  static styles = `
    :host { display: block; }
    .card {
      position: relative;
      max-width: 32rem;
      margin: 1.5rem auto;
      padding: 1.75rem 1.5rem 1.5rem;
      background: var(--rm-surface);
      border: 1px solid var(--rm-border);
      border-radius: 0.75rem;
      text-align: center;
    }
    .lock {
      display: inline-flex; align-items: center; justify-content: center;
      width: 3rem; height: 3rem; margin-bottom: 0.75rem;
      border-radius: 50%;
      background: color-mix(in srgb, var(--rm-accent) 18%, transparent);
      color: var(--rm-accent);
      font-size: 1.5rem;
    }
    h3 { margin: 0 0 0.5rem; font-size: 1.35rem; color: var(--rm-fg); }
    p.body { margin: 0 auto 1.25rem; max-width: 26rem; color: var(--rm-muted); line-height: 1.5; }
    .cta {
      display: inline-block;
      padding: 0.75rem 1.75rem;
      background: var(--rm-accent);
      color: var(--rm-bg);
      border: none;
      border-radius: 0.5rem;
      font: inherit; font-weight: 600; font-size: 1rem;
      cursor: pointer;
    }
    .cta:hover { filter: brightness(1.08); }
    .signin {
      display: inline-block; margin-top: 0.9rem;
      background: none; border: none; padding: 0.25rem 0.5rem;
      color: var(--rm-muted); font: inherit; font-size: 0.85rem;
      text-decoration: underline; cursor: pointer;
    }
    .signin:hover { color: var(--rm-fg); }
    .dismiss {
      position: absolute; top: 0.6rem; right: 0.6rem;
      display: inline-flex; align-items: center; justify-content: center;
      width: 2rem; height: 2rem; padding: 0;
      background: transparent; border: none; border-radius: 0.4rem;
      color: var(--rm-muted); font-size: 1.1rem; cursor: pointer;
    }
    .dismiss:hover { background: var(--rm-surface-hover); color: var(--rm-fg); }
    button:focus-visible, a:focus-visible { outline: 2px solid var(--rm-accent); outline-offset: 2px; }
    .lock svg, .dismiss svg { display: block; }
  `;

  render() {
    const copy = COPY[this.reason] ?? COPY.locked;
    const lock = document.createElement("span");
    lock.className = "lock";
    lock.setAttribute("aria-hidden", "true");
    lock.innerHTML = LOCK;

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.className = "dismiss";
    dismiss.id = "dismiss";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.innerHTML = CLOSE;

    return html`
      <div
        class="card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-headline"
      >
        ${dismiss}
        ${lock}
        <h3 id="upgrade-headline">${copy.headline}</h3>
        <p class="body">${copy.body}</p>
        <button class="cta" id="cta" type="button">${copy.cta}</button>
        <div>
          <button class="signin" id="signin" type="button">Already purchased? Sign in →</button>
        </div>
      </div>
    `;
  }

  onConnected() {
    const root = this.shadowRoot;
    if (!root) return;

    const cta = /** @type {HTMLButtonElement | null} */ (root.getElementById("cta"));
    const dismiss = /** @type {HTMLButtonElement | null} */ (root.getElementById("dismiss"));

    if (cta) {
      const onCta = () => this._checkout();
      cta.addEventListener("click", onCta);
      this.addCleanup(() => cta.removeEventListener("click", onCta));
    }
    if (dismiss) {
      const onDismiss = () => this._dismiss();
      dismiss.addEventListener("click", onDismiss);
      this.addCleanup(() => dismiss.removeEventListener("click", onDismiss));
    }

    const signin = /** @type {HTMLButtonElement | null} */ (root.getElementById("signin"));
    if (signin) {
      const onSignin = () => this._signin();
      signin.addEventListener("click", onSignin);
      this.addCleanup(() => signin.removeEventListener("click", onSignin));
    }

    // Keyboard: Esc dismisses; Tab is trapped inside the two focusable
    // controls so focus can't escape behind the modal.
    /** @type {EventListener} */
    const onKeydown = (e) => {
      const ev = /** @type {KeyboardEvent} */ (e);
      if (ev.key === "Escape") {
        ev.preventDefault();
        this._dismiss();
        return;
      }
      if (ev.key === "Tab") this._trapTab(ev);
    };
    this.addEventListener("keydown", onKeydown);
    this.addCleanup(() => this.removeEventListener("keydown", onKeydown));

    // Move focus into the card so keyboard/screen-reader users land on the CTA.
    cta?.focus();
  }

  /**
   * Keep Tab focus cycling between the CTA and the dismiss control.
   * @param {KeyboardEvent} ev
   */
  _trapTab(ev) {
    const root = this.shadowRoot;
    if (!root) return;
    /** @type {HTMLElement[]} */
    const focusables = [
      /** @type {HTMLElement | null} */ (root.getElementById("cta")),
      /** @type {HTMLElement | null} */ (root.getElementById("signin")),
      /** @type {HTMLElement | null} */ (root.getElementById("dismiss")),
    ].filter(/** @returns {el is HTMLElement} */ (el) => el != null);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = root.activeElement;

    if (ev.shiftKey && active === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  _checkout() {
    // Send the buyer to the sales page's payment section (STRIPE_CHECKOUT_URL,
    // build-time define). `_top` so that when the app is embedded in an iframe
    // (the speechgenie.co study page) the navigation breaks OUT of the frame to
    // the full sales page rather than loading it inside the player. Standalone,
    // `_top` is just the current window. The CTA click provides the user
    // activation a cross-origin frame needs to navigate top.
    window.open(STRIPE_CHECKOUT_URL, "_top");
  }

  /** "Already purchased? Sign in" — send the user to the WordPress login page
   *  (magic-link). `_top` so it breaks out of the studio-page iframe when
   *  embedded; the current window when standalone. */
  _signin() {
    window.open(SIGN_IN_URL, "_top");
  }

  /** Dismiss the upsell: notify the host, then (unless embedded) go back to
   *  where the user came from. The player screen mounts this in place of the
   *  transport, so Back is the natural "close"; the list mounts it as an
   *  overlay (`embedded`) and removes it on the `dismiss` event instead. */
  _dismiss() {
    this.emit("dismiss");
    if (!this.embedded) history.back();
  }
}
